import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/DashboardBits";
import PincodeInput from "@/components/common/PincodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Loader2, MapPin, X } from "lucide-react";

export default function ManagersPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const [openTerritory, setOpenTerritory] = useState(null);
  const load = () => api.get("/managers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Managers" subtitle={`${list.length} active`} right={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-manager-button"><Plus className="h-4 w-4 mr-1" /> Add Manager</Button></DialogTrigger>
          <FormDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
        </Dialog>
      } />
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
              <TableHead>Territory (pincodes)</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No managers yet.</TableCell></TableRow>)}
              {list.map((m) => (
                <TableRow key={m.id} data-testid={`manager-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{m.email}</TableCell>
                  <TableCell className="text-slate-600">{m.phone}</TableCell>
                  <TableCell className="text-slate-600 text-xs">
                    {(m.allowed_pincodes || []).slice(0, 4).map((p) => <span key={p} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-slate-100 rounded">{p}</span>)}
                    {(m.allowed_pincodes || []).length > 4 && <span className="text-slate-400">+{m.allowed_pincodes.length - 4} more</span>}
                    {(m.allowed_pincodes || []).length === 0 && <span className="text-red-500">No pincodes</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenTerritory(m)} data-testid={`manager-territory-${m.id}`}><MapPin className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setOpenUser(m)} data-testid={`manager-create-user-${m.id}`}><KeyRound className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={async () => {
                      if (!window.confirm("Delete manager?")) return;
                      try { await api.delete(`/managers/${m.id}`); toast.success("Deleted"); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`manager-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {openUser && <CreateUserDialog entity={openUser} role="manager" onClose={() => setOpenUser(null)} />}
      {openTerritory && <TerritoryDialog manager={openTerritory} onClose={() => setOpenTerritory(null)} onSaved={() => { setOpenTerritory(null); load(); }} />}
    </div>
  );
}

function FormDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", allowed_pincodes: [] });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/managers", f); toast.success("Manager added"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle className="font-heading">Add Manager</DialogTitle><DialogDescription>Assign territory pincodes after creating.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mgr-name" /></div>
        <div><Label>Email</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="mgr-email" /></div>
        <div><Label>Phone</Label><Input required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="mgr-phone" /></div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="mgr-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function TerritoryDialog({ manager, onClose, onSaved }) {
  const [pins, setPins] = useState(manager.allowed_pincodes || []);
  const [current, setCurrent] = useState("");
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState(false);

  const addPin = () => {
    if (!resolved || !resolved.isRajasthan) { toast.error("Enter a valid Rajasthan pincode"); return; }
    if (pins.includes(resolved.pincode)) { toast.info("Already added"); return; }
    setPins([...pins, resolved.pincode]);
    setCurrent(""); setResolved(null);
  };
  const removePin = (p) => setPins(pins.filter((x) => x !== p));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/managers/${manager.id}/pincodes`, { pincodes: pins }); toast.success("Territory saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-heading">Territory — {manager.name}</DialogTitle><DialogDescription>Pincodes this manager can operate in.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <PincodeInput value={current} onChange={(v) => setCurrent(v)} onResolved={(r) => setResolved(r)} label="Add pincode" testId="territory-pincode" />
          <Button type="button" onClick={addPin} className="bg-blue-600 hover:bg-blue-700" data-testid="territory-add-button"><Plus className="h-4 w-4 mr-1" /> Add to territory</Button>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Current territory ({pins.length})</div>
            {pins.length === 0 ? <p className="text-sm text-slate-500">No pincodes yet — manager cannot see any leads or executives.</p> : (
              <div className="flex flex-wrap gap-2">
                {pins.map((p) => (
                  <span key={p} className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-sm" data-testid={`territory-pin-${p}`}>
                    {p}
                    <button type="button" onClick={() => removePin(p)} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={save} disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="territory-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save territory"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ entity, role, onClose }) {
  const [form, setForm] = useState({ email: entity?.email || "", password: "", name: entity?.name || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.toLowerCase(), password: form.password, name: form.name,
        role, entity_id: entity.id, phone: entity.phone,
      });
      toast.success("Login user created");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Create Manager Login</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary Password</Label><Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
